import {
  LinkOptions,
  NavigateOptions,
  RegisteredRouter,
} from '@tanstack/react-router';
import { InboxMessage } from 'services/inbox/inbox.types';
import { Comparator } from 'utils/types/sort';
import { InboxViews, ReadFilter } from './Inbox.config';
import {
  InboxSearchSearch,
  InboxUrlState,
  ReadFilterSubsets,
} from './Inbox.types';

export const computeReadFilter = (
  readFilter: string | undefined,
): ReadFilter => {
  switch (readFilter) {
    case ReadFilter.notRead:
      return ReadFilter.notRead;
    case ReadFilter.all:
      return ReadFilter.all;
    case ReadFilter.alreadyRead:
      return ReadFilter.alreadyRead;
    default:
      return ReadFilter.all;
  }
};

/**
 * Reducer of the inbox url.
 * @param pathParams path Url params
 * @param queryParams path query Params
 */
export function mapUrlToInboxViews(search: InboxSearchSearch): InboxUrlState {
  const {
    inboxSection,
    messageId: messageIdParam,
    readFilter: readFIlterFromParams,
  } = search;
  const isValidInboxSection = inboxSection === 'message';
  const readFilter = computeReadFilter(readFIlterFromParams);

  if (isValidInboxSection) {
    return {
      inboxView: InboxViews.showMessage,
      messageId: parseInt(messageIdParam || '', 10),
      readFilter,
    };
  }

  return {
    inboxView: InboxViews.showAllMessages,
    messageId: NaN,
    readFilter,
  };
}

export function createInboxMessageUrl(
  message: InboxMessage,
  readFilter: ReadFilter,
): NavigateOptions<RegisteredRouter, string, '/inbox'> {
  return {
    to: '/inbox',
    search: {
      inboxSection: 'message',
      messageId: message.idMessage.toString(),
      ...(readFilter !== ReadFilter.all ? { readFilter } : {}),
    },
  };
}

export function createInboxLink(
  readFilter: ReadFilter,
): LinkOptions<RegisteredRouter, string, '/inbox'> {
  return {
    to: '/inbox',
    search: {
      ...(readFilter !== ReadFilter.all ? { readFilter } : {}),
    },
  };
}

/**
 * Groups messages by read filter value.
 * If a comparator is provided sorts the groups.
 * @param messages All the inbox messages
 * @param comparator sorts the output.
 */
export function computeReadFilterSubsets(
  messages: InboxMessage[],
  comparator?: Comparator<InboxMessage>,
): ReadFilterSubsets {
  const output = {
    [ReadFilter.all]: messages,
    [ReadFilter.notRead]: [] as InboxMessage[],
    [ReadFilter.alreadyRead]: [] as InboxMessage[],
  };

  for (let i = 0, len = messages.length; i < len; i++) {
    const message = messages[i];
    if (message.readFlag) {
      output[ReadFilter.alreadyRead].push(message);
    } else {
      output[ReadFilter.notRead].push(message);
    }
  }

  if (typeof comparator === 'function') {
    output[ReadFilter.all] = output[ReadFilter.all].slice(0).sort(comparator);
    output[ReadFilter.alreadyRead] =
      output[ReadFilter.alreadyRead].sort(comparator);
    output[ReadFilter.notRead] = output[ReadFilter.notRead].sort(comparator);
  }

  return output;
}
